import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../../../../../@core/services/toast.service';
import { ScoringService } from '../../../../../../../../../../@shared/services/scoring.service';
import {
  EventClassResponse,
  EventClassRegistrationResponse,
  ClassScoringConfigResponse,
  ClassRoundResponse,
  ClassResultResponse,
  ResultImageResponse
} from '../../../../../../../../../../@shared/models/response.module';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-result-entry-modal',
  standalone: false,
  templateUrl: './result-entry-modal.html',
  styleUrl: './result-entry-modal.css'
})
export class ResultEntryModal implements OnInit {
  @Input() eventClass: EventClassResponse;
  @Input() registration: EventClassRegistrationResponse;
  @Input() round: ClassRoundResponse;
  @Input() scheduleEntryId?: number;
  @Input() existingResult?: ClassResultResponse;

  scoringConfig: ClassScoringConfigResponse | null = null;

  // Images
  resultImages: ResultImageResponse[] = [];
  uploadingImage = false;

  form: FormGroup;
  loading = true;
  saving = false;

  statusOptions = [
    { value: 'recorded', labelKey: 'results.recorded' },
    { value: 'dnf', labelKey: 'results.dnf' },
    { value: 'dns', labelKey: 'results.dns' },
    { value: 'dq', labelKey: 'results.dq' }
  ];

  constructor(
    private fb: FormBuilder,
    private modal: NgbActiveModal,
    private scoringService: ScoringService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.form = this.fb.group({
      raw_time_seconds: [null],
      raw_points: [null],
      penalty_total: [0],
      status: ['recorded'],
      notes: ['']
    });
  }

  ngOnInit() {
    this.loadData();
  }

  async loadData() {
    this.loading = true;
    try {
      // Load scoring config
      try {
        this.scoringConfig = await firstValueFrom(
          this.scoringService.getScoringConfig({ event_class_id: this.eventClass.event_class_id })
        );
      } catch (e) {
        // Default config
      }

      // If editing existing result, populate form
      if (this.existingResult) {
        this.form.patchValue({
          raw_time_seconds: this.existingResult.raw_time_seconds,
          raw_points: this.existingResult.raw_points,
          penalty_total: this.existingResult.penalty_total || 0,
          status: this.existingResult.status,
          notes: this.existingResult.notes || ''
        });

        // Load existing images
        try {
          this.resultImages = await firstValueFrom(
            this.scoringService.getResultImages({ result_id: this.existingResult.result_id })
          ) || [];
        } catch (e) {
          this.resultImages = [];
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.loading = false;
    }
  }

  get isTimeBased(): boolean {
    return this.scoringConfig?.score_type === 'timed' || this.scoringConfig?.score_type === 'combined';
  }

  get isPointsBased(): boolean {
    return this.scoringConfig?.score_type === 'points_high' ||
           this.scoringConfig?.score_type === 'points_low' ||
           this.scoringConfig?.score_type === 'combined';
  }

  get participantName(): string {
    const user = this.registration?.user;
    if (!user) return 'Unknown';
    return user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown';
  }

  get horseName(): string {
    return this.registration?.horse?.name || '';
  }

  getPenaltyLabel(): string {
    if (this.scoringConfig?.penalty_type === 'time') {
      return this.translateService.instant('results.penaltySeconds');
    }
    return this.translateService.instant('results.penaltyPoints');
  }

  getPenaltyHint(): string {
    if (this.scoringConfig?.penalty_type === 'time') {
      return this.translateService.instant('results.penaltySecondsHint');
    }
    return this.translateService.instant('results.penaltyPointsHint');
  }

  async save() {
    if (this.saving) return;

    this.saving = true;
    try {
      const formValue = this.form.value;

      let resultId: number;

      if (this.existingResult) {
        // Update existing result
        await firstValueFrom(
          this.scoringService.updateResult({
            result_id: this.existingResult.result_id,
            raw_time_seconds: formValue.raw_time_seconds,
            raw_points: formValue.raw_points,
            status: formValue.status,
            notes: formValue.notes
          })
        );
        resultId = this.existingResult.result_id;
      } else {
        // Create new result
        const result = await firstValueFrom(
          this.scoringService.createResult({
            class_registration_id: this.registration.class_registration_id,
            round_id: this.round.round_id,
            schedule_entry_id: this.scheduleEntryId,
            raw_time_seconds: formValue.raw_time_seconds,
            raw_points: formValue.raw_points,
            status: formValue.status,
            notes: formValue.notes
          })
        );
        resultId = result?.result_id;
      }

      // Apply penalty if set
      const penaltyValue = parseFloat(formValue.penalty_total) || 0;
      if (penaltyValue > 0 && resultId) {
        try {
          await firstValueFrom(
            this.scoringService.applyPenalties({
              result_id: resultId,
              penalties: [{
                penalty_name: 'Manual',
                penalty_value: penaltyValue
              }]
            })
          );
        } catch (penaltyError) {
          console.error('Error applying penalty:', penaltyError);
          // Result saved but penalty failed - show warning
          this.toastService.warning(this.translateService.instant('results.penaltySaveError'));
        }
      }

      this.toastService.success(this.translateService.instant('results.savedSuccess'));
      this.modal.close(true);
    } catch (error) {
      console.error('Error saving result:', error);
      this.toastService.error(this.translateService.instant('results.saveError'));
    } finally {
      this.saving = false;
    }
  }

  // Image methods
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadImage(input.files[0]);
      input.value = ''; // Reset input
    }
  }

  async uploadImage(file: File) {
    if (!this.existingResult?.result_id) {
      this.toastService.error(this.translateService.instant('images.saveResultFirst'));
      return;
    }

    this.uploadingImage = true;
    try {
      const image = await firstValueFrom(
        this.scoringService.uploadResultImage({
          result_id: this.existingResult.result_id,
          image: file,
          image_name: file.name
        })
      );
      if (image) {
        this.resultImages = [...this.resultImages, image];
        this.toastService.success(this.translateService.instant('images.uploadSuccess'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      this.toastService.error(this.translateService.instant('images.uploadError'));
    } finally {
      this.uploadingImage = false;
    }
  }

  async deleteImage(image: ResultImageResponse) {
    try {
      await firstValueFrom(
        this.scoringService.deleteResultImage({ image_id: image.image_id })
      );
      this.resultImages = this.resultImages.filter(i => i.image_id !== image.image_id);
      this.toastService.success(this.translateService.instant('images.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting image:', error);
      this.toastService.error(this.translateService.instant('images.deleteError'));
    }
  }

  viewImage(image: ResultImageResponse) {
    // Open image in lightbox or new tab
    window.open(image.image_url, '_blank');
  }

  close() {
    this.modal.dismiss();
  }
}
